// Base Libs
import React, { useContext, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import {
    GuxDropdownMulti,
    GuxListboxMulti,
    GuxOptionMulti,
    GuxIcon,
    GuxFormFieldCheckbox,
    GuxTruncate,
    GuxTooltip,
} from "genesys-spark-components-react";
// Styles
import "./MultipleSelectQuestionOptions.less";
// Main commons
import { useAppDispatch, useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
// Feature commons
import { ReadOnlyCheckbox } from "features/common/ReadOnlyInput";
import { MarkdownInline } from "features/common/Markdown";
import { ScoreTag } from "features/common/ScoreTag";
import { useStrings } from "features/i18n/useStrings";
import {
    CheckboxModeContext,
    PreviewModeContext,
    UserModeContext,
} from "features/qualityForm/common/Contexts";
import {
    AssistedMultipleSelectQuestionOptionsDropdown,
    AssistedMultipleSelectQuestionOptionCheckbox,
} from "features/qualityForm/formViewer/evaluationAssistance/AssistedMultipleSelectQuestionOption";
// Feature slices
import { selectQuestionGroupIdContainingQuestion } from "features/qualityForm/qualityFormSlice";
import {
    getMultipleSelectQuestionOptionsMapping,
    getQuestionTotalScore,
    selectAnswerOption,
    selectFormType,
    selectQuestion,
    selectAssistedAnswerIdsForMSQ,
    selectCurrentAnswerIdsForMSQ,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    scorableSliceActions,
    getPreviewScores,
    selectIsScorableComplete,
    selectOptionLevel,
} from "features/scorable/scorableSlice";
// Locales
import strings from "./CommonQuestionComponents.strings";
import { uniqueId } from "lodash";
import { getScoreValueAriaLabel } from "util/componentUtil";

export function MultipleSelectQuestionDropdownOption({
    questionId,
    multipleSelectOptionId,
    showScore,
}: {
    questionId: string;
    multipleSelectOptionId: string;
    showScore: boolean;
}) {
    const t = useStrings(strings);
    const { text, value } = useAppSelector((state) => {
        const { text, value } = selectAnswerOption(
            state,
            multipleSelectOptionId,
            true
        ) ?? {
            text: "",
            value: 0.0,
        };
        return { text, value };
    }, shallowEqual);
    const questionLevel = useAppSelector((state) => {
        return selectOptionLevel(state, questionId, multipleSelectOptionId);
    });
    const [showTooltip, setShowTooltip] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);
    const randomId = uniqueId();
    useEffect(() => {
        if (showTooltip) {
            ref.current?.dispatchEvent(new Event("focusin"));
        }
    }, [showTooltip]);
    return text ? (
        <GuxOptionMulti value={multipleSelectOptionId}>
            <div
                className={`MultipleSelectQuestionDropdownOptionContent${
                    showScore ? "WithScoreTag" : ""
                }`}
                onPointerEnter={() => {
                    const element = ref.current;
                    if (element && element.scrollWidth > element.clientWidth) {
                        setShowTooltip(true);
                    }
                }}
                onPointerLeave={() => setShowTooltip(false)}
                onFocus={() => {
                    const element = ref.current;
                    if (element && element.scrollWidth > element.clientWidth) {
                        setShowTooltip(true);
                    }
                }}
                onBlur={() => setShowTooltip(false)}
            >
                <span id={randomId} ref={ref}>
                    {text}
                </span>
                {showScore && (
                    <>
                        <div></div>
                        <div className="OptionScoreTag">
                            <ScoreTag
                                mode={questionLevel}
                                selected={false}
                                ariaLabel={getScoreValueAriaLabel(
                                    t.labels,
                                    questionLevel,
                                    value ?? 0
                                )}
                            >
                                {value}
                            </ScoreTag>
                        </div>
                    </>
                )}
            </div>
            {showTooltip && <GuxTooltip for={randomId}>{text}</GuxTooltip>}
        </GuxOptionMulti>
    ) : (
        <></>
    );
}
// For Checkbox configuration entries
export function MultipleSelectQuestionOption({
    questionId,
    multipleSelectOptionId,
    disabled,
    selected,
    onSelected,
    hideScore = false,
}: {
    questionId: string;
    multipleSelectOptionId: string;
    disabled: boolean;
    selected: boolean;
    onSelected: (s: string) => void;
    hideScore?: boolean;
}) {
    const t = useStrings(strings);
    const { text, value } = useAppSelector((state) => {
        const { text, value } = selectAnswerOption(
            state,
            multipleSelectOptionId,
            true
        ) ?? {
            text: "",
            value: 0.0,
        };
        return { text, value };
    }, shallowEqual);
    const questionLevel = useAppSelector((state) => {
        return selectOptionLevel(state, questionId, multipleSelectOptionId);
    });
    return (
        <div
            className={`MultipleSelectQuestionOption ${
                selected ? "selected" : ""
            }`}
            data-testid="multiple-select-question-option"
        >
            {disabled ? (
                <>
                    <ReadOnlyCheckbox checked={selected} label={text} />
                    {!hideScore && (
                        <ScoreTag
                            mode={questionLevel}
                            selected={selected}
                            ariaLabel={getScoreValueAriaLabel(
                                t.labels,
                                questionLevel,
                                value ?? 0
                            )}
                        >
                            {value}
                        </ScoreTag>
                    )}
                </>
            ) : (
                <>
                    <GuxFormFieldCheckbox>
                        <input
                            slot="input"
                            type="checkbox"
                            id={multipleSelectOptionId}
                            checked={selected}
                            onChange={({ target }) => onSelected(target.id)}
                        />
                        {text?.trim() === "" ? (
                            <label
                                slot="label"
                                htmlFor={multipleSelectOptionId}
                                className="invalid-input-placeholder"
                            >
                                {t.labels.defaultAnswerText}
                                <GuxIcon
                                    iconName="fa/hexagon-exclamation-solid"
                                    screenreaderText={t.labels.invalidInput}
                                ></GuxIcon>
                            </label>
                        ) : (
                            <label
                                slot="label"
                                htmlFor={multipleSelectOptionId}
                                className="editor-answer-option-label"
                            >
                                <GuxTruncate
                                    max-lines={15}
                                    data-testid="answer-option-text"
                                >
                                    <MarkdownInline
                                        markdownInlineText={text ?? ""}
                                    ></MarkdownInline>
                                </GuxTruncate>
                            </label>
                        )}
                    </GuxFormFieldCheckbox>
                </>
            )}
        </div>
    );
}
export default function MultipleSelectQuestionOptions({
    questionId,
    disabled = true,
    hideScore = false,
}: {
    questionId: string;
    disabled?: boolean;
    hideScore?: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const { previewMode } = useContext(PreviewModeContext);
    const { checkboxMode } = useContext(CheckboxModeContext);
    const multipleSelectOptionIdList: string[] =
        useAppSelector(
            (state) =>
                selectQuestion(state, questionId)?.multipleSelectOptionQuestions
        ) ?? [];
    const answerOptionIdMappings = useAppSelector((state) => {
        return getMultipleSelectQuestionOptionsMapping(state, questionId);
    });
    const isFinished = useAppSelector((state) => {
        return selectIsScorableComplete(state);
    });
    const questionGroupId = useAppSelector(
        (state) =>
            selectQuestionGroupIdContainingQuestion(questionId)(
                state.qualityForm
            ) ?? ""
    );
    const selectedAnswerOptionIds = useAppSelector((state) => {
        return selectCurrentAnswerIdsForMSQ(state, questionId);
    });
    const saveCheckboxAnswer = (newSelectedAnswerId: string) => {
        dispatch(
            scorableSliceActions.updateMultipleSelectAnswers({
                questionGroupId,
                questionId,
                checkboxAnswerId: newSelectedAnswerId,
                dropdownAnswerIdList: undefined,
                answerIdsMapping: answerOptionIdMappings,
            })
        );
        if (previewMode) {
            dispatch(getPreviewScores({ isScorableFinished: false }));
        }
    };
    const { isAdmin } = useContext(UserModeContext);
    const isEvaluationScoringView = useAppSelector((state) => {
        return selectFormType(state) === FormType.EVALUATION;
    });
    const answerScoreValue = useAppSelector((state) => {
        return getQuestionTotalScore(state, questionId, true);
    });
    // get the list of assistedAnswerIds for every answer option
    // check if there's at least one answer option selected by the evaluation assistance
    const assistedAnswerIds = useAppSelector((state) => {
        return selectAssistedAnswerIdsForMSQ(state, questionId);
    });
    // if isFinished & disabled, then always display in checkbox
    // otherwise, it depends whether the user enabled the toggle
    const showInCheckboxMode = isFinished && disabled ? true : checkboxMode;
    const multipleSelectAnswerOptionCheckbox = (
        multipleSelectOptionId: string
    ) => {
        if (isEvaluationScoringView) {
            if (isAdmin && assistedAnswerIds[multipleSelectOptionId]) {
                return (
                    <div
                        className="AssistedMultipleSelectQuestionOptionGroup"
                        data-testid="assisted-multiple-select-question-option-group"
                    >
                        {/* TODO: determine whether to show only one icon when there's multiple answer options with evaluation assistance configured */}
                        <div className="evaluation-assistance-quote">
                            <GuxIcon
                                iconName="quote-left"
                                data-testid="evaluation-assistance-quote"
                                screenreader-text={t.labels.assistedAnswer}
                            />
                            <GuxTooltip>{t.labels.assistedAnswer}</GuxTooltip>
                        </div>
                        <AssistedMultipleSelectQuestionOptionCheckbox
                            questionId={questionId}
                            answerOptionId={multipleSelectOptionId}
                            selected={
                                selectedAnswerOptionIds?.includes(
                                    multipleSelectOptionId
                                ) ?? false
                            }
                            key={multipleSelectOptionId}
                            onSelected={saveCheckboxAnswer}
                            disabled={disabled}
                            hideScore={hideScore}
                        />
                    </div>
                );
            }
            return (
                <MultipleSelectQuestionOption
                    questionId={questionId}
                    multipleSelectOptionId={multipleSelectOptionId}
                    disabled={disabled}
                    selected={
                        selectedAnswerOptionIds?.includes(
                            multipleSelectOptionId
                        ) ?? false
                    }
                    key={multipleSelectOptionId}
                    onSelected={saveCheckboxAnswer}
                    hideScore={hideScore}
                />
            );
        } else {
            return <></>;
        }
    };
    const multipleSelectAnswerOptionCheckboxList = () => {
        return (
            <>
                {multipleSelectOptionIdList.map((mSelectOptId) => {
                    return mSelectOptId ? (
                        <React.Fragment key={mSelectOptId}>
                            {multipleSelectAnswerOptionCheckbox(mSelectOptId)}
                        </React.Fragment>
                    ) : (
                        <></>
                    );
                })}
            </>
        );
    };

    const [sortedOptions, setSortedOptions] = useState<string[]>();
    const getReorderedOptions = () => {
        const selectedIdSet = new Set(selectedAnswerOptionIds);
        const unselectedIds: string[] = [];
        const selectedIds: string[] = [];
        multipleSelectOptionIdList.forEach((id) => {
            if (selectedIdSet.has(id)) {
                selectedIds.push(id);
            } else {
                unselectedIds.push(id);
            }
        });
        return (selectedIds ?? []).concat(unselectedIds ?? []);
    };

    useEffect(() => {
        setSortedOptions(getReorderedOptions());
    }, []);

    const updateDropdownAnswers = (
        e: React.FormEvent<HTMLGuxDropdownMultiElement>
    ) => {
        if (!disabled) {
            const selectedValue = (e.target as HTMLInputElement).value;
            const selectedAnswerOptions =
                selectedValue === undefined ? [] : selectedValue.split(",");
            dispatch(
                scorableSliceActions.updateMultipleSelectAnswers({
                    questionGroupId,
                    questionId,
                    checkboxAnswerId: undefined,
                    dropdownAnswerIdList: selectedAnswerOptions,
                    answerIdsMapping: answerOptionIdMappings,
                })
            );
            if (previewMode) {
                dispatch(
                    getPreviewScores({
                        isScorableFinished: false,
                    })
                );
            }
        }
    };

    const multipleSelectAnswerOptionDropdown = () => {
        return (
            sortedOptions && (
                <GuxDropdownMulti
                    data-testid="multiple-select-dropdown"
                    filterType="starts-with"
                    placeholder={t.labels.multipleSelectPlaceholder}
                    value={(selectedAnswerOptionIds || []).join(",")}
                    onInput={(e) => updateDropdownAnswers(e)}
                    onGuxcollapsed={() => {
                        setSortedOptions(getReorderedOptions());
                    }}
                >
                    <GuxListboxMulti
                        aria-label={t.labels.multipleSelectAriaLabel}
                    >
                        {sortedOptions.map((mSelectOptId) => {
                            if (mSelectOptId) {
                                return (
                                    <MultipleSelectQuestionDropdownOption
                                        questionId={questionId}
                                        multipleSelectOptionId={mSelectOptId}
                                        key={mSelectOptId}
                                        showScore={disabled && !hideScore}
                                    />
                                );
                            }
                        })}
                    </GuxListboxMulti>
                </GuxDropdownMulti>
            )
        );
    };
    return (
        <div className="MultipleSelectQuestionOptions">
            {showInCheckboxMode ? (
                <div
                    className="MultipleSelectQuestionCheckboxes"
                    data-testid="multiple-select-question-checkboxes"
                >
                    {multipleSelectAnswerOptionCheckboxList()}
                </div>
            ) : (
                <>
                    {Object.keys(assistedAnswerIds).length > 0 ? (
                        <AssistedMultipleSelectQuestionOptionsDropdown
                            questionId={questionId}
                            disabled={disabled}
                            hideScore={hideScore}
                            sortedOptions={sortedOptions ?? []}
                            onInput={updateDropdownAnswers}
                            onCollapsed={() =>
                                setSortedOptions(getReorderedOptions())
                            }
                        />
                    ) : (
                        <div className="MultipleSelectQuestionDropdown">
                            {multipleSelectAnswerOptionDropdown()}
                            {disabled && !hideScore && (
                                <ScoreTag
                                    mode="default"
                                    selected={
                                        selectedAnswerOptionIds.length > 0
                                    }
                                    ariaLabel={t.labels.scoreValue({
                                        value: answerScoreValue,
                                    })}
                                >
                                    {answerScoreValue}
                                </ScoreTag>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
